import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MSFLogo from '../components/MSFLogo';
import PageBackground from '../components/PageBackground';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Zap, Wind, Shield, HardHat, Cpu, Car, Bike, ShoppingCart,
  ChevronRight, Trash2, ArrowRight, Check, X, Wrench, Package
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  zap: Zap, wind: Wind, shield: Shield, 'hard-hat': HardHat, cpu: Cpu,
};

const EFFECT_COLORS = {
  double_points: 'text-yellow-400',
  flat_bonus: 'text-blue-400',
  negate_dnf: 'text-green-400',
  halve_negative: 'text-orange-400',
  bonus_50pct: 'text-purple-400',
};

const EFFECT_BG = {
  double_points: 'bg-yellow-500/10 border-yellow-500/30',
  flat_bonus: 'bg-blue-500/10 border-blue-500/30',
  negate_dnf: 'bg-green-500/10 border-green-500/30',
  halve_negative: 'bg-orange-500/10 border-orange-500/30',
  bonus_50pct: 'bg-purple-500/10 border-purple-500/30',
};

export default function Workshop() {
  const [user, setUser] = useState(null);
  const [activeLeague, setActiveLeague] = useState('racing');
  const [boostDefs, setBoostDefs] = useState([]);
  const [myBoosts, setMyBoosts] = useState([]);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const [selection, setSelection] = useState(null);
  const [entities, setEntities] = useState({});
  const [loading, setLoading] = useState(true);
  const [applyModal, setApplyModal] = useState(null);
  const [purchasing, setPurchasing] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [userRes, boostsRes, myBoostsRes, budgetRes, selectionRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/workshop/boosts?league=${activeLeague}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/workshop/my-boosts?league=${activeLeague}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/workshop/budget-info?league=${activeLeague}`, { withCredentials: true }).catch(() => ({ data: null })),
        activeLeague === 'racing'
          ? axios.get(`${BACKEND_URL}/api/my-selection`, { withCredentials: true }).catch(() => ({ data: null }))
          : axios.get(`${BACKEND_URL}/api/moto/my-selection`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);

      setUser(userRes.data);
      setBoostDefs(boostsRes.data);
      setMyBoosts(myBoostsRes.data);
      setBudgetInfo(budgetRes.data);
      setSelection(selectionRes.data);

      // Fetch entity details
      if (selectionRes.data) {
        const sel = selectionRes.data;
        const entityMap = {};
        if (activeLeague === 'racing') {
          const [driversRes, carsRes, tunersRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/drivers`, { withCredentials: true }),
            axios.get(`${BACKEND_URL}/api/cars`, { withCredentials: true }),
            axios.get(`${BACKEND_URL}/api/tuners`, { withCredentials: true }),
          ]);
          driversRes.data.filter(d => sel.drivers?.includes(d.driver_id)).forEach(d => { entityMap[d.driver_id] = { ...d, type: 'driver', label: d.name }; });
          carsRes.data.filter(c => sel.cars?.includes(c.car_id)).forEach(c => { entityMap[c.car_id] = { ...c, type: 'car', label: c.name }; });
          const tuner = tunersRes.data.find(t => (t.tuner_id || t.team_id) === sel.tuner);
          if (tuner) entityMap[sel.tuner] = { ...tuner, type: 'tuner', label: tuner.name };
        } else {
          const [ridersRes, bikesRes, tunersRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/moto/riders`, { withCredentials: true }),
            axios.get(`${BACKEND_URL}/api/moto/bikes`, { withCredentials: true }),
            axios.get(`${BACKEND_URL}/api/moto/tuners`, { withCredentials: true }),
          ]);
          ridersRes.data.filter(r => sel.riders?.includes(r.rider_id)).forEach(r => { entityMap[r.rider_id] = { ...r, type: 'rider', label: r.name }; });
          bikesRes.data.filter(b => sel.bikes?.includes(b.bike_id)).forEach(b => { entityMap[b.bike_id] = { ...b, type: 'bike', label: b.name }; });
          const tuner = tunersRes.data.find(t => t.tuner_id === sel.tuner);
          if (tuner) entityMap[sel.tuner] = { ...tuner, type: 'moto_tuner', label: tuner.name };
        }
        setEntities(entityMap);
      }
    } catch (err) {
      console.error('Workshop fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeLeague]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePurchase = async (boostId) => {
    setPurchasing(boostId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/workshop/purchase?boost_id=${boostId}&league=${activeLeague}`,
        {}, { withCredentials: true }
      );
      toast.success('Boost purchased!');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const handleApply = async (instanceId, entityType, entityId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/workshop/apply?instance_id=${instanceId}&entity_type=${entityType}&entity_id=${entityId}`,
        {}, { withCredentials: true }
      );
      toast.success('Boost applied!');
      setApplyModal(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    }
  };

  const handleRemove = async (instanceId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/workshop/remove?instance_id=${instanceId}`,
        {}, { withCredentials: true }
      );
      toast.success('Boost removed');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleSell = async (instanceId) => {
    if (!window.confirm('Sell this boost? Credits will be returned to your budget.')) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/workshop/sell?instance_id=${instanceId}`,
        {}, { withCredentials: true }
      );
      toast.success(res.data.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sell');
    }
  };

  const accentColor = activeLeague === 'racing' ? 'primary' : 'secondary';
  const ownedBoostIds = myBoosts.map(b => b.boost_id);
  const appliedEntityIds = myBoosts.filter(b => b.applied_to_id).map(b => b.applied_to_id);

  const getTeamEntities = () => {
    if (!selection) return [];
    const ids = activeLeague === 'racing'
      ? [...(selection.drivers || []), ...(selection.cars || []), selection.tuner].filter(Boolean)
      : [...(selection.riders || []), ...(selection.bikes || []), selection.tuner].filter(Boolean);
    return ids.map(id => entities[id]).filter(Boolean);
  };

  return (
    <PageBackground pageKey="workshop">
    <div className="min-h-screen">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-black text-5xl mb-2" data-testid="workshop-title">
            <span className={`text-${accentColor}`}>WORKSHOP</span>
          </h1>
          <p className="text-zinc-400">Upgrade your team with performance boosts</p>
        </div>

        {/* League Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveLeague('racing')}
            data-testid="workshop-racing-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeLeague === 'racing' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Car size={20} />
            <MSFLogo suffix="Racing" size="sm" />
          </button>
          <button
            onClick={() => setActiveLeague('moto')}
            data-testid="workshop-moto-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeLeague === 'moto' ? 'bg-secondary text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Bike size={20} />
            <MSFLogo suffix="Moto" size="sm" />
          </button>
        </div>

        {/* Budget Bar */}
        {budgetInfo && (
          <div className="bg-card border border-zinc-800 p-4 mb-8" data-testid="workshop-budget-bar">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-400">Budget</span>
              <span className="text-sm font-bold">
                <span className="text-white">{budgetInfo.remaining?.toLocaleString()}</span>
                <span className="text-zinc-500"> / {budgetInfo.budget_limit?.toLocaleString()} remaining</span>
              </span>
            </div>
            <div className="w-full h-2 bg-zinc-800 overflow-hidden">
              <div className="h-full flex">
                <div className={`bg-${accentColor} transition-all`} style={{ width: `${(budgetInfo.team_cost / budgetInfo.budget_limit) * 100}%` }} />
                <div className="bg-green-500 transition-all" style={{ width: `${(budgetInfo.boost_cost / budgetInfo.budget_limit) * 100}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><span className={`w-2 h-2 bg-${accentColor} inline-block`} /> Team: {budgetInfo.team_cost?.toLocaleString()}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 inline-block" /> Boosts: {budgetInfo.boost_cost?.toLocaleString()}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="bg-card border border-zinc-800 p-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-zinc-400">Loading workshop...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Boost Shop */}
            <div className="lg:col-span-2">
              <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                <ShoppingCart size={20} className={`text-${accentColor}`} />
                Available Parts
                <span className="text-xs text-zinc-500 font-normal ml-2">({myBoosts.length}/2 slots used)</span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {boostDefs.map((boost) => {
                  const owned = ownedBoostIds.includes(boost.boost_id);
                  const IconComp = ICON_MAP[boost.icon] || Zap;
                  const effectColor = EFFECT_COLORS[boost.effect_type] || 'text-zinc-300';
                  const effectBg = EFFECT_BG[boost.effect_type] || '';
                  const canAfford = budgetInfo && budgetInfo.remaining >= boost.price;
                  const slotsFull = myBoosts.length >= 2;

                  return (
                    <div
                      key={boost.boost_id}
                      data-testid={`boost-card-${boost.boost_id}`}
                      className={`relative border overflow-hidden transition-all ${
                        owned ? 'bg-zinc-900/50 border-green-600/50' : effectBg || 'bg-card border-zinc-800'
                      } ${!owned && canAfford && !slotsFull ? 'hover:border-zinc-600' : ''}`}
                    >
                      {owned && (
                        <div className="absolute top-2 right-2 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 uppercase">
                          Owned
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-start gap-3 mb-3">
                          <div className={`w-12 h-12 flex items-center justify-center ${effectBg || 'bg-zinc-800'} border`}>
                            <IconComp size={24} className={effectColor} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold text-base">{boost.name}</h3>
                            <p className={`text-sm ${effectColor} font-medium`}>{boost.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-4">
                          <span className="font-heading font-bold text-lg text-white">{boost.price.toLocaleString()} <span className="text-xs text-zinc-500">credits</span></span>
                          {owned ? (
                            <span className="text-green-500 text-sm font-bold flex items-center gap-1"><Check size={16} /> Purchased</span>
                          ) : (
                            <button
                              onClick={() => handlePurchase(boost.boost_id)}
                              disabled={purchasing === boost.boost_id || !canAfford || slotsFull}
                              data-testid={`buy-boost-${boost.boost_id}`}
                              className={`px-4 py-2 text-sm font-bold uppercase transition-all ${
                                !canAfford ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed' :
                                slotsFull ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed' :
                                `bg-${accentColor} ${accentColor === 'secondary' ? 'text-black' : 'text-white'} hover:opacity-80`
                              }`}
                            >
                              {purchasing === boost.boost_id ? 'Buying...' : !canAfford ? 'No Budget' : slotsFull ? 'Slots Full' : 'Buy'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: My Boosts / Inventory */}
            <div>
              <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                <Package size={20} className="text-green-500" />
                My Workshop
              </h2>

              {myBoosts.length === 0 ? (
                <div className="bg-card border border-zinc-800 p-8 text-center">
                  <Wrench className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No boosts yet</p>
                  <p className="text-zinc-600 text-sm mt-1">Purchase parts from the shop to upgrade your team</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myBoosts.map((boost) => {
                    const def = boostDefs.find(d => d.boost_id === boost.boost_id) || {};
                    const IconComp = ICON_MAP[def.icon] || Zap;
                    const effectColor = EFFECT_COLORS[def.effect_type] || 'text-zinc-300';
                    const appliedEntity = boost.applied_to_id ? entities[boost.applied_to_id] : null;

                    return (
                      <div key={boost.instance_id} data-testid={`my-boost-${boost.instance_id}`} className="bg-card border border-zinc-800 p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <IconComp size={20} className={effectColor} />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">{def.name}</p>
                            <p className={`text-xs ${effectColor}`}>{def.description}</p>
                          </div>
                        </div>

                        {appliedEntity ? (
                          <div className="bg-zinc-900 border border-zinc-800 p-3 mb-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <ArrowRight size={14} className="text-green-500" />
                                <span className="text-sm">Applied to <span className="font-bold text-white">{appliedEntity.label}</span></span>
                                <span className="text-[10px] uppercase bg-zinc-800 px-1.5 py-0.5 text-zinc-400">{boost.applied_to_type}</span>
                              </div>
                              <button
                                onClick={() => handleRemove(boost.instance_id)}
                                data-testid={`remove-boost-${boost.instance_id}`}
                                className="text-xs text-zinc-500 hover:text-red-400 transition-colors"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setApplyModal(boost)}
                            data-testid={`apply-boost-${boost.instance_id}`}
                            className={`w-full py-2 text-sm font-bold uppercase border border-dashed border-zinc-700 text-zinc-400 hover:border-${accentColor} hover:text-${accentColor} transition-all mb-3`}
                          >
                            Apply to Entity
                          </button>
                        )}

                        <button
                          onClick={() => handleSell(boost.instance_id)}
                          data-testid={`sell-boost-${boost.instance_id}`}
                          className="flex items-center gap-1 text-xs text-red-500/70 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={12} /> Sell ({def.price?.toLocaleString()} credits)
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Team Boost Overview */}
              {selection && Object.keys(entities).length > 0 && (
                <div className="mt-6">
                  <h3 className="font-heading font-bold text-sm text-zinc-400 uppercase mb-3">Team Boost Status</h3>
                  <div className="space-y-1">
                    {getTeamEntities().map((ent) => {
                      const entityId = ent.driver_id || ent.car_id || ent.tuner_id || ent.team_id || ent.rider_id || ent.bike_id;
                      const appliedBoost = myBoosts.find(b => b.applied_to_id === entityId);
                      const boostDef = appliedBoost ? boostDefs.find(d => d.boost_id === appliedBoost.boost_id) : null;
                      const BIcon = boostDef ? (ICON_MAP[boostDef.icon] || Zap) : null;

                      return (
                        <div key={entityId} className="flex items-center justify-between py-2 px-3 bg-zinc-900/50 border border-zinc-800/50 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase text-zinc-600 w-12">{ent.type}</span>
                            <span className="text-zinc-300">{ent.label}</span>
                          </div>
                          {boostDef ? (
                            <span className={`flex items-center gap-1 text-xs font-medium ${EFFECT_COLORS[boostDef.effect_type]}`}>
                              <BIcon size={12} /> {boostDef.name}
                            </span>
                          ) : (
                            <span className="text-xs text-zinc-700">No boost</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Apply Boost Modal */}
      <Dialog open={!!applyModal} onOpenChange={() => setApplyModal(null)}>
        <DialogContent className="bg-zinc-950 border border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-lg">Apply {applyModal && boostDefs.find(d => d.boost_id === applyModal.boost_id)?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400 mb-4">Choose a team member to apply this boost to:</p>
          <div className="space-y-2">
            {getTeamEntities().map((ent) => {
              const entityId = ent.driver_id || ent.car_id || ent.tuner_id || ent.team_id || ent.rider_id || ent.bike_id;
              const alreadyBoosted = appliedEntityIds.includes(entityId);

              return (
                <button
                  key={entityId}
                  onClick={() => applyModal && handleApply(applyModal.instance_id, ent.type, entityId)}
                  disabled={alreadyBoosted}
                  data-testid={`apply-to-${entityId}`}
                  className={`w-full flex items-center justify-between p-3 border transition-all ${
                    alreadyBoosted
                      ? 'border-zinc-800 bg-zinc-900/50 text-zinc-600 cursor-not-allowed'
                      : `border-zinc-800 hover:border-${accentColor} bg-zinc-900 hover:bg-zinc-800`
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase bg-zinc-800 px-1.5 py-0.5 text-zinc-400">{ent.type}</span>
                    <span className="font-medium">{ent.label}</span>
                  </div>
                  {alreadyBoosted ? (
                    <span className="text-xs text-zinc-600">Already boosted</span>
                  ) : (
                    <ChevronRight size={16} className="text-zinc-500" />
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PageBackground>
  );
}
